<template>
  <div
    :tabindex="0"
    :class="[
      'fs-select',
      {
        'fs-select--opened': isOpened,
        'fs-select--disabled': isDisabled,
        'fs-select--left': position === 'left',
        'fs-select--right': position === 'right',
        'fs-select--mid': variant === 'mid',
        'fs-select--min-code': variant === 'min-code',
      },
      position && variant === 'mid' ? `fs-select--mid--${position}` : '',
      position && variant === 'min-code' ? `fs-select--min-code--${position}` : '',
    ]"
    @keydown.enter="toggleDropdown($event)"
    @keydown.esc="closeDropdown"
    @focus="isSearchable ? $refs.select.focus() : undefined"
  >
    <select-value
      ref="select"
      v-model:value="localValue"
      v-model:search-query="searchQuery"
      class="fs-select__select"
      :is-opened="isOpened"
      :is-searchable="isSearchable"
      :is-clearable="isClearable"
      :placeholder="placeholder"
      :position="position"
      :variant="variant"
      @toggle="toggleDropdown"
      @clear="$emit('clear')"
    >
      <template v-if="$slots['search-prefix']" #search-prefix><slot name="search-prefix"></slot></template>
      <slot v-if="valueIsSet" name="selected" :selected="selectedValue">
        <fs-tooltip :text="getOptionLabel(selectedValue)?.toString()">
          {{ getOptionLabel(selectedValue) }}
        </fs-tooltip>
      </slot>
    </select-value>

    <fs-popper
      ref="popper"
      :offset="[0, -selectHeight]"
      animation="none"
      :placement="position === 'left' ? 'bottom-start' : 'bottom-end'"
      is-close-on-esc
      no-styling
      @open="isOpened = true"
      @close="isOpened = false"
      @clear="$emit('clear')"
      @placement-changed="onPlacementChanged"
    >
      <div
        :class="{
          popper: true,
          'popper--flip': isFlipped,
          'popper--left': position === 'left',
          'popper--right': position === 'right',
          'popper--mid': variant === 'mid',
          'popper--min-code': variant === 'min-code',
          'fs-select__popper': true,
        }"
        :style="{ width: `${isOpened ? selectWidth * expandedWidthScale : selectWidth}px` }"
      >
        <select-value
          v-model:search-query="searchQuery"
          v-model:value="localValue"
          class="fs-select__select"
          :is-opened="isOpened"
          :is-searchable="isSearchable"
          :is-clearable="isClearable"
          :placeholder="placeholder"
          :position="position"
          :variant="variant"
          @toggle="toggleDropdown"
          @keydown.enter="selectFirstOption"
          @clear="$emit('clear')"
        >
          <template v-if="$slots['search-prefix']" #search-prefix><slot name="search-prefix"></slot></template>
          <slot v-if="valueIsSet" name="selected" :selected="selectedValue">
            {{ getOptionLabel(selectedValue) }}
          </slot>
        </select-value>

        <div
          v-if="isOpened"
          ref="dropdown"
          :class="{
            dropdown: true,
            'dropdown--flip': isFlipped,
            'dropdown--tight': Boolean(position),
            'dropdown--mid': variant === 'mid',
            'dropdown--min-code': variant === 'min-code',
            popper__dropdown: true,
          }"
        >
          <fs-scrollable
            class="dropdown__scrollable"
            shadows-color="dark-gray"
            :style="{ maxHeight: maxDropdownHeight + 'px' }"
          >
            <div v-if="searchQuery && filteredOptions.length === 0" class="dropdown__no-results">
              <span v-html="icons.singles.noSearchResults" />
              <span>{{ noResultsText }}</span>
            </div>
            <ul v-else class="dropdown__list">
              <!--
                mousedown is prevented so picking an option leaves focus where it is: the
                dropdown is teleported out of the select, so letting it take focus tells
                whatever wraps the select that the user has left it.
              -->
              <template v-for="(option, index) in filteredOptions" :key="index">
                <li v-if="sections.titleAt.has(index)" class="dropdown__section-title">
                  {{ sections.titleAt.get(index) }}
                </li>
                <li
                  :class="{
                    'dropdown__list-item': true,
                    'dropdown__list-item--tight': Boolean(position),
                  }"
                  @mousedown.prevent
                  @click="selectOption($event, option)"
                >
                  <span class="dropdown__list-item-value">
                    <slot :option="option">
                      <fs-tooltip :text="getOptionLabel(option)">
                        {{ getOptionLabel(option) }}
                      </fs-tooltip>
                    </slot>
                  </span>

                  <span
                    v-if="lodashIsEqual(value ? option[value] : option, localValue)"
                    class="dropdown__selected-checkmark"
                    v-html="icons.singles.checkmark"
                  />
                </li>
              </template>
            </ul>
          </fs-scrollable>
          <div v-if="footerActions.length" class="dropdown__divider"></div>
          <div class="dropdown-footer">
            <ul v-if="footerActions.length" class="dropdown-footer__action-list">
              <li
                v-for="(action, index) in footerActions"
                :key="index"
                class="dropdown-footer__label"
                @click="
                  action.callback($event);
                  closeDropdown();
                "
              >
                <span v-if="action.icon" class="dropdown-footer__label-icon" v-html="action.icon" />

                <span class="dropdown-footer__label-name">
                  {{ action.name }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </fs-popper>
  </div>
</template>

<script>
import twoWayProperty from '@lib/twoWayProperty';
import { groupBySection } from '@lib/group-by-section';
import lodashIsEqual from 'lodash/isEqual';

// Components
import FsPopper from '@components/poppers/fs-popper.vue';
import FsScrollable from '@components/fs-scrollable.vue';
import SelectValue from './select-value.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';

// Icons
import checkmark from '@icons/other/ico-selected.svg';
import noSearchResults from '@icons/other2/no-search-results.svg';

export default {
  name: 'fs-select',

  emits: ['update:modelValue', 'clear', 'change', 'open', 'close', 'search'],

  props: {
    /**
     * Contains the currently selected value.
     * @type {Object||String||null}
     */
    modelValue: {},

    /**
     * An array of strings or objects to be used as dropdown choices.
     * If you are using an array of objects, fs-select will look for
     * a `label` key (ex. [{label: 'This is Foo', value: 'foo'}]). A
     * custom label key can be set with the `label` prop.
     * @type {Array}
     */
    options: {
      type: Array,
      default() {
        return [];
      },
    },

    /**
     * Tells fs-select what key to use when generating option
     * labels when each `option` is an object.
     * @type {String}
     */
    label: {
      type: String,
      default: 'label',
    },

    /**
     * Optional key to use for search filtering. When provided, this key
     * is used to filter options instead of the label key. Useful when you
     * want to search on different data than what's displayed.
     * @type {String}
     */
    searchLabel: {
      type: String,
      default: null,
    },

    /**
     * Tells fs-select what key to use for the modelValue.
     * If not set, then uses the entire option as value.
     */
    value: {
      type: String,
      default: null,
    },

    /**
     * Returns the section an option is listed under, by default its `section` key. Options without
     * one come first, then each section under its title.
     * @type {Function}
     */
    sectionOf: {
      type: Function,
      default: (option) => option?.section,
    },

    /**
     * Equivalent to the `placeholder` attribute on an `<input>`.
     * @type {String}
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * Defines the dropdown max height in pixels.
     * @type {Number}
     */
    maxDropdownHeight: {
      type: Number,
      default: 180,
    },

    /**
     * Enable/disable filtering the options.
     * @type {Boolean}
     */
    isSearchable: {
      type: Boolean,
      default: false,
    },

    /**
     * Disabled the select field.
     * @type {Boolean}
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows clearing the selected value
     * will be ignored if `isSearchable` is true.
     * @type {Boolean}
     */
    isClearable: {
      type: Boolean,
      default: false,
    },

    /**
     * Text to show when searchable and no results were found.
     * @type {String}
     */
    noResultsText: {
      type: String,
      default: 'Nothing was found',
    },

    /**
     * Additional actions in the dropdown footer.
     * An action has a name, optional icon, and a callback function:
     * (ex. [{name: 'Create new', icon: 'add', callback: () => {}}])
     *
     * Note: This covers our needs at the moment, if it doesn't in the future - consider
     * adding custom footer as a slot instead.
     * @type {Array}
     */
    footerActions: {
      type: Array,
      default() {
        return [];
      },
    },

    position: {
      type: String,
      validator: (position) => ['left', 'right'].includes(position),
    },

    /**
     * Use when the fs-select should be wider when opened than closed.
     * If twice as wide when opened, set to 2. Default is 1, meaning
     * the fs-select will keep the same width when opened.
     * @type {Boolean}
     */
    expandedWidthScale: {
      type: Number,
      default: 1,
    },

    /**
     * Visual variant of the select.
     * - 'standard': Default appearance
     * - 'mid': Medium size appearance
     * - 'min-code': Compact appearance for dense tables
     * @type {String}
     */
    variant: {
      type: String,
      default: 'standard',
      validator: (value) => ['standard', 'mid', 'min-code'].includes(value),
    },
  },

  components: {
    FsPopper,
    FsScrollable,
    SelectValue,
    FsTooltip,
  },

  data() {
    return {
      isOpened: false,
      openDown: true,
      isFlipped: false,

      searchQuery: this.modelValue ? this.getOptionLabel(this.getOption(this.modelValue)) : null,

      selectHeight: null,
      selectWidth: null,

      icons: {
        singles: {
          checkmark,
          noSearchResults,
        },
      },
    };
  },

  mounted() {
    window.addEventListener('resize', this.calculateSelectDimensions);

    this.$nextTick(() => {
      this.calculateSelectDimensions();
    });
  },

  unmounted() {
    window.removeEventListener('resize', this.calculateSelectDimensions);
  },

  computed: {
    localValue: twoWayProperty('modelValue'),

    matchingOptions() {
      if (!this.isSearchable || !this.searchQuery) {
        return this.options;
      }

      return this.options.filter((option) => {
        return this.getOptionSearchLabel(option).toLowerCase().includes(this.searchQuery.toLowerCase());
      });
    },

    sections() {
      return groupBySection(this.matchingOptions, this.sectionOf);
    },

    filteredOptions() {
      return this.sections.items;
    },

    valueIsSet() {
      return this.localValue !== null && this.localValue !== undefined;
    },

    selectedValue() {
      if (this.valueIsSet) return this.getOption(this.localValue);

      return undefined;
    },
  },

  methods: {
    lodashIsEqual,

    openDropdown() {
      if (this.isDisabled) return;

      this.calculateSelectDimensions();
      this.$refs.popper.open({ target: this.$refs.select.$el });
    },

    closeDropdown() {
      this.$refs.popper.close();
      this.$el?.focus();
    },

    toggleDropdown() {
      if (this.isDisabled) return;

      this.calculateSelectDimensions();
      this.$refs.popper.toggle({ target: this.$refs.select.$el });
    },

    onPlacementChanged(placement) {
      this.isFlipped = placement.includes('top');
    },

    selectOption(event, option) {
      this.localValue = this.value ? option[this.value] : option;

      if (this.isSearchable) {
        this.searchQuery = this.getOptionLabel(option);
      }

      this.isOpened = false;
      this.$emit('change', option);

      this.closeDropdown();
    },

    selectFirstOption(event) {
      this.selectOption(event, this.filteredOptions[0]);
    },

    getOption(value) {
      if (this.value) {
        return this.options.find((option) => option[this.value] === value);
      } else {
        return value;
      }
    },

    getOptionLabel(option) {
      if (typeof option !== 'object') {
        return option;
      }

      if (option.hasOwnProperty(this.label)) {
        return option[this.label];
      }

      return console.warn(
        `[fs-select warn]: Label key "option.${this.label}" does not` +
          ` exist in options object ${JSON.stringify(option)}.\n`
      );
    },

    getOptionSearchLabel(option) {
      const labelKey = this.searchLabel || this.label;

      if (typeof option !== 'object') {
        return option;
      }

      if (option.hasOwnProperty(labelKey)) {
        return option[labelKey];
      }

      return console.warn(
        `[fs-select warn]: Search label key "option.${labelKey}" does not` +
          ` exist in options object ${JSON.stringify(option)}.\n`
      );
    },

    calculateSelectDimensions() {
      // $refs.select can be gone if a queued nextTick/resize fires after the component unmounts
      // (e.g. an fs-select inside a parent that remounts via :key).
      const el = this.$refs.select?.$el;
      if (!el) return;
      this.selectHeight = el.clientHeight;
      this.selectWidth = el.clientWidth;
    },

    focus() {
      this.$refs.select?.focus();
    },
  },

  watch: {
    isOpened(value) {
      this.$emit(value ? 'open' : 'close');
    },

    searchQuery(value) {
      this.$emit('search', value);

      if (value?.length) {
        this.openDropdown();
      } else {
        this.closeDropdown();
        this.$refs.select.focus();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/text';

.dropdown-footer {
  &__action-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  &__label {
    display: flex;
    align-items: center;
    padding: 3px 1.2rem 0.4rem;
    border-radius: 0.5rem;
    cursor: pointer;

    &:hover {
      background-color: rgba($c-dark, 0.1);
    }
  }

  &__label-icon {
    margin-right: 0.6rem;

    :deep(svg) {
      min-width: 1.4rem;
      width: 1.4rem;
      height: 1.4rem;
      fill: $c-dark;
    }
  }

  &__label-name {
    font-size: 1.4rem;
    line-height: 1.6rem;
    color: $c-dark;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// Dropdown ------------------------------------------------------------
.dropdown {
  padding: 0.4rem 1.2rem 0.8rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &--flip {
    padding: 0.8rem 1.2rem 0.4rem;
    flex-direction: column-reverse;
  }

  &--tight {
    padding-left: 0.8rem;
    padding-right: 0.8rem;
  }

  &__scrollable {
  }

  &__no-results {
    margin: 1.2rem 0 2rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: rgba($c-dark, 0.5);

    :deep(svg) {
      width: 1.8rem;
      height: 1.8rem;
      margin-right: 1.2rem;
      fill: rgba($c-dark, 0.5);
    }
  }

  &__list {
    margin: 0;
    padding: 0;
  }

  &__section-title {
    @include option-group-title;
    margin: 0.7rem 0 0.2rem 1.2rem;
  }

  &__list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1.2rem;
    border-radius: 0.5rem;
    cursor: pointer;

    &--tight {
      padding: 0.5rem 0.8rem;
    }

    &:hover {
      background-color: rgba($c-dark, 0.1);
    }
  }

  &__list-item-value {
    margin-right: 1rem;
    flex-grow: 1;
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: $c-dark;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &--mid &__list-item-value {
    font-size: 1.2rem;
    line-height: 1.4rem;
  }

  &--mid &__list-item {
    padding: 0.4rem 1rem;
  }

  &--min-code &__list-item-value {
    font-size: 1.2rem;
    line-height: 1.4rem;
  }

  &--min-code &__list-item {
    padding: 0.3rem 0.6rem;
  }

  &__selected-checkmark {
    width: 1.3rem;
    height: 1rem;
    fill: $c-dark;
    flex-shrink: 0;
  }

  &__divider {
    width: calc(100% + 2.4rem);
    border-bottom: 1px rgba($c-dark, 0.1) solid;
    margin: 0.8rem -1.2rem;
  }
}

// Popper ------------------------------------------------------------
.popper {
  display: flex;
  flex-direction: column;
  border-radius: 1.9rem;
  background-color: $c-bg-gray-darkest;
  box-shadow: 0 1px 0.8rem rgb(0 0 0 / 5%);

  &--left {
    border-radius: 1.9rem 0 1.9rem 1.9rem;
  }

  &--right {
    border-radius: 0 1.9rem 1.9rem;
  }

  &--flip {
    flex-direction: column-reverse;
  }

  &--right#{&}--flip {
    border-radius: 1.9rem 1.9rem 1.9rem 0;
  }

  &--left#{&}--flip {
    border-radius: 1.9rem 1.9rem 0 1.9rem;
  }

  &--expanded {
    width: 150%;
  }

  &--mid {
    border-radius: 1.6rem;
  }

  &--mid#{&}--left {
    border-radius: 1.6rem 0 1.6rem 1.6rem;
  }

  &--mid#{&}--right {
    border-radius: 0 1.6rem 1.6rem 1.6rem;
  }

  &--mid#{&}--flip {
    border-radius: 1.6rem;
  }

  &--mid#{&}--right#{&}--flip {
    border-radius: 1.6rem 1.6rem 1.6rem 0;
  }

  &--mid#{&}--left#{&}--flip {
    border-radius: 1.6rem 1.6rem 0 1.6rem;
  }

  &--min-code {
    border-radius: 0.5rem;
  }

  &__dropdown {
  }
}
// FS Select ------------------------------------------------------------
.fs-select {
  height: 3.7rem;
  max-width: 100%;
  position: relative;
  z-index: 1;
  border-radius: 1.9rem;
  cursor: pointer;
  user-select: none;
  background-color: rgba($c-bg-gray-darkest, 0.5);
  transition:
    background-color 0.15s,
    box-shadow 0.15s;
  cursor: pointer;

  &--left {
    border-radius: 1.9rem 0 0 1.9rem;
  }

  &--right {
    border-radius: 0 1.9rem 1.9rem 0;
  }

  &:hover {
    background-color: rgba($c-bg-gray-darkest, 0.75);
  }

  &:focus {
    outline: none;
    background-color: $c-bg-gray-darkest;
  }

  // Hide the select when the dropdown is open
  // since popper placement is not 100% overlapping in all screen sizes,
  // we don't want duplicated visible edges
  &--opened {
    opacity: 0;
    pointer-events: none;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--mid {
    height: 3.1rem;
    border-radius: 1.6rem;
  }

  &--mid--left {
    border-radius: 1.6rem 0 0 1.6rem;
  }

  &--mid--right {
    border-radius: 0 1.6rem 1.6rem 0;
  }

  &--min-code {
    height: 2.5rem;
    border-radius: 0.5rem;
  }

  &--min-code--left {
    border-radius: 0.5rem 0 0 0.5rem;
  }

  &--min-code--right {
    border-radius: 0 0.5rem 0.5rem 0;
  }

  &__select {
  }

  &__popper {
  }
}
</style>
